import { useEffect, useState } from 'react';
import { Mail, Smartphone } from 'lucide-react';
import type { NotificationChannelsSettings } from '@techsofit/api-types';
import { Card, Skeleton, Input, Button, cn } from '@techsofit/ui';
import {
  getNotificationChannels,
  sendMemberAttendanceEmailsNow,
  sendMonthlyReportsNow,
  sendWeeklyReportsNow,
  updateNotificationChannels,
} from '../lib/adminApi';
import { useAsync } from '../lib/useAsync';
import { SentMessagesTab } from './SentMessages';

type Tab = 'channels' | 'sent';

/** Notifications hub: channel settings + the log of everything we've sent, as tabs. */
export function SettingsPage() {
  const [tab, setTab] = useState<Tab>('channels');

  return (
    <section className="mt-6 animate-fade-up">
      <div className="mb-4">
        <h1 className="font-display text-display-md text-ink">Notifications</h1>
        <p className="mt-1 text-sm text-ink-soft">
          Choose which channels send messages, and review everything that's been sent.
        </p>
      </div>

      <div className="mb-5 inline-flex gap-1 rounded-pill bg-surface p-1">
        {(
          [
            { key: 'channels', label: 'Channels' },
            { key: 'sent', label: 'Sent messages' },
          ] as const
        ).map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={cn(
              'rounded-pill px-4 py-1.5 text-sm font-medium transition-colors',
              tab === t.key ? 'bg-card text-ink shadow-card' : 'text-ink-soft hover:text-ink',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'channels' ? <ChannelsTab /> : <SentMessagesTab />}
    </section>
  );
}

type ChannelKey = 'emailEnabled' | 'smsEnabled';

const CHANNELS: { key: ChannelKey; icon: typeof Mail; title: string; body: string }[] = [
  {
    key: 'emailEnabled',
    icon: Mail,
    title: 'Email',
    body: 'Receipts and reminders sent by email. The reliable default — leave on.',
  },
  {
    key: 'smsEnabled',
    icon: Smartphone,
    title: 'SMS',
    body: 'Text messages via Sendai. Sent only for the allow-listed events (membership expiring/expired, gym subscription reminders) — never for goal check-ins or weekly attendance recaps. Email is the default.',
  },
];

/** Events that can be sent over SMS. Toggling one off = that event falls back to email everywhere. */
const SMS_EVENT_OPTIONS: { key: string; label: string }[] = [
  { key: 'MEMBERSHIP_EXPIRING', label: 'Membership expiring' },
  { key: 'MEMBERSHIP_EXPIRED', label: 'Membership expired' },
  { key: 'SUBSCRIPTION_EXPIRING', label: 'Gym subscription expiring' },
  { key: 'SUBSCRIPTION_PAST_DUE', label: 'Gym subscription past due' },
];

/** Which channels are allowed to send, platform-wide. */
function ChannelsTab() {
  const { data, loading } = useAsync(getNotificationChannels);
  const [ch, setCh] = useState<NotificationChannelsSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data) setCh(data);
  }, [data]);

  async function persist(next: NotificationChannelsSettings, prev: NotificationChannelsSettings) {
    setCh(next);
    setSaving(true);
    setSaved(false);
    try {
      const res = await updateNotificationChannels({
        emailEnabled: next.emailEnabled,
        smsEnabled: next.smsEnabled,
        whatsappEnabled: false, // WhatsApp is shelved — always off
        smsEvents: next.smsEvents,
      });
      setCh(res);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch {
      setCh(prev); // revert on failure
    } finally {
      setSaving(false);
    }
  }

  function toggle(key: ChannelKey) {
    if (!ch || saving) return;
    persist({ ...ch, [key]: !ch[key] }, ch);
  }

  function toggleEvent(eventKey: string) {
    if (!ch || saving) return;
    const has = ch.smsEvents.includes(eventKey);
    const smsEvents = has ? ch.smsEvents.filter((e) => e !== eventKey) : [...ch.smsEvents, eventKey];
    persist({ ...ch, smsEvents }, ch);
  }

  return (
    <div>
      {saved && <p className="mb-2 text-sm font-medium text-ok-ink">Saved</p>}

      <div className="flex flex-col gap-3">
        {CHANNELS.map((c) => {
          const on = !!ch?.[c.key];
          return (
            <Card key={c.key} className="flex items-center gap-4 p-5">
              <span
                className={cn(
                  'inline-flex size-10 shrink-0 items-center justify-center rounded-control',
                  on ? 'bg-ink text-on-dark' : 'bg-surface text-ink-soft',
                )}
              >
                <c.icon className="size-5" aria-hidden />
              </span>
              <div className="min-w-0 flex-1">
                <h2 className="font-display text-lg font-semibold text-ink">{c.title}</h2>
                <p className="mt-0.5 text-sm text-muted">{c.body}</p>
              </div>
              {loading ? (
                <Skeleton className="h-6 w-11 rounded-full" />
              ) : (
                <Toggle on={on} disabled={saving} onChange={() => toggle(c.key)} label={c.title} />
              )}
            </Card>
          );
        })}
      </div>

      <p className="mt-4 text-xs text-muted">
        SMS costs us per message, so it's used only for the allow-listed events below and only for gyms on an SMS
        plan; everyone else gets email. Turn SMS off above to fall back to email everywhere.
      </p>

      {ch?.smsEnabled && (
        <Card className="mt-4 p-5">
          <h2 className="font-display text-lg font-semibold text-ink">Events allowed on SMS</h2>
          <p className="mt-0.5 text-sm text-muted">
            Everything else is emailed. Goal check-ins and weekly attendance recaps are email-only — they
            used to blow through SMS credit.
          </p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {SMS_EVENT_OPTIONS.map((o) => {
              const on = !!ch?.smsEvents.includes(o.key);
              return (
                <label
                  key={o.key}
                  className={cn(
                    'flex cursor-pointer items-center justify-between rounded-control border border-line px-3 py-2 text-sm',
                    saving && 'opacity-60',
                  )}
                >
                  <span className="text-ink">{o.label}</span>
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={saving}
                    onChange={() => toggleEvent(o.key)}
                    className="size-4 accent-ink"
                  />
                </label>
              );
            })}
          </div>
        </Card>
      )}

      {ch && <BillingAlerts ch={ch} onSaved={setCh} />}
      {ch && <WeeklyReports ch={ch} onSaved={setCh} />}
      {ch && <MemberAttendanceEmails ch={ch} onSaved={setCh} />}
    </div>
  );
}

function BillingAlerts({
  ch,
  onSaved,
}: {
  ch: NotificationChannelsSettings;
  onSaved: (next: NotificationChannelsSettings) => void;
}) {
  const [to, setTo] = useState(ch.billingNotifyTo ?? 'info@techsolutely.co.zw');
  const [cc, setCc] = useState(ch.billingNotifyCc ?? '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setTo(ch.billingNotifyTo ?? 'info@techsolutely.co.zw');
    setCc(ch.billingNotifyCc ?? '');
  }, [ch.billingNotifyTo, ch.billingNotifyCc]);

  async function save() {
    setSaving(true);
    setSaved(false);
    try {
      const res = await updateNotificationChannels({
        emailEnabled: ch.emailEnabled,
        smsEnabled: ch.smsEnabled,
        whatsappEnabled: false,
        smsEvents: ch.smsEvents,
        billingNotifyTo: to.trim() || 'info@techsolutely.co.zw',
        billingNotifyCc: cc.trim(),
      });
      onSaved(res);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="mt-4 p-5">
      <h2 className="font-display text-lg font-semibold text-ink">Billing alerts</h2>
      <p className="mt-0.5 text-sm text-muted">
        When a gym pays for its TechSoFit subscription, we email the gym owner an invoice and send a
        hooray to this inbox.
      </p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Input label="Notify (to)" type="email" value={to} onChange={(e) => setTo(e.target.value)} />
        <Input
          label="CC (optional)"
          type="email"
          value={cc}
          onChange={(e) => setCc(e.target.value)}
          placeholder="optional"
        />
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Button size="sm" onClick={save} loading={saving}>
          Save billing alerts
        </Button>
        {saved && <p className="text-sm font-medium text-ok-ink">Saved</p>}
      </div>
    </Card>
  );
}

function WeeklyReports({
  ch,
  onSaved,
}: {
  ch: NotificationChannelsSettings;
  onSaved: (next: NotificationChannelsSettings) => void;
}) {
  const [enabled, setEnabled] = useState(ch.weeklyReportEnabled ?? true);
  const [internalTo, setInternalTo] = useState(ch.weeklyReportInternalTo ?? '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingMonthly, setSendingMonthly] = useState(false);
  const [sentNote, setSentNote] = useState<string | null>(null);

  useEffect(() => {
    setEnabled(ch.weeklyReportEnabled ?? true);
    setInternalTo(ch.weeklyReportInternalTo ?? '');
  }, [ch.weeklyReportEnabled, ch.weeklyReportInternalTo]);

  async function save() {
    setSaving(true);
    setSaved(false);
    try {
      const res = await updateNotificationChannels({
        emailEnabled: ch.emailEnabled,
        smsEnabled: ch.smsEnabled,
        whatsappEnabled: false,
        smsEvents: ch.smsEvents,
        weeklyReportEnabled: enabled,
        weeklyReportInternalTo: internalTo.trim(),
      });
      onSaved(res);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } finally {
      setSaving(false);
    }
  }

  async function sendNow() {
    setSending(true);
    setSentNote(null);
    try {
      const r = await sendWeeklyReportsNow();
      setSentNote(
        `Sent last week (${r.weekStart} – ${r.weekEnd}): ${r.ownerEmails} owner emails, ${r.internalEmails} internal copies across ${r.gyms} gyms. Owners were not CC’d.`,
      );
    } catch {
      setSentNote('Could not send the weekly reports.');
    } finally {
      setSending(false);
    }
  }

  async function sendMonthlyNow() {
    setSendingMonthly(true);
    setSentNote(null);
    try {
      const r = await sendMonthlyReportsNow();
      setSentNote(
        `Sent last month (${r.weekStart} – ${r.weekEnd}): ${r.ownerEmails} owner emails, ${r.internalEmails} internal copies across ${r.gyms} gyms. Owners were not CC’d.`,
      );
    } catch {
      setSentNote('Could not send the monthly reports.');
    } finally {
      setSendingMonthly(false);
    }
  }

  return (
    <Card className="mt-4 p-5">
      <h2 className="font-display text-lg font-semibold text-ink">Weekly &amp; monthly gym reports</h2>
      <p className="mt-0.5 text-sm text-muted">
        Every Monday at 08:00 we email each gym owner a PDF of last week’s performance, and on the
        1st at 08:30 a report for the previous month. Internal copies go out as a separate email —
        gym owners never see TechSoFit addresses.
      </p>
      <div className="mt-4 flex items-center gap-3">
        <Toggle
          on={enabled}
          disabled={saving}
          onChange={() => setEnabled(!enabled)}
          label="Email owners"
        />
        <span className="text-sm font-medium text-ink">Email gym owners every Monday</span>
      </div>
      <div className="mt-4">
        <Input
          label="Internal copy (To, comma-separated)"
          value={internalTo}
          onChange={(e) => setInternalTo(e.target.value)}
          placeholder="Leave empty to skip the internal copy"
          hint="Never CC’d onto the owner email. Empty this list if volume starts looking like spam."
        />
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button size="sm" onClick={save} loading={saving}>
          Save weekly reports
        </Button>
        <Button size="sm" variant="outline" onClick={sendNow} loading={sending}>
          Send last week now
        </Button>
        <Button size="sm" variant="outline" onClick={sendMonthlyNow} loading={sendingMonthly}>
          Send last month now
        </Button>
        {saved && <p className="text-sm font-medium text-ok-ink">Saved</p>}
      </div>
      {sentNote && <p className="mt-3 text-sm text-ink-soft">{sentNote}</p>}
    </Card>
  );
}

function MemberAttendanceEmails({
  ch,
  onSaved,
}: {
  ch: NotificationChannelsSettings;
  onSaved: (next: NotificationChannelsSettings) => void;
}) {
  const [enabled, setEnabled] = useState(ch.memberAttendanceEmailEnabled ?? true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentNote, setSentNote] = useState<string | null>(null);

  useEffect(() => {
    setEnabled(ch.memberAttendanceEmailEnabled ?? true);
  }, [ch.memberAttendanceEmailEnabled]);

  async function save() {
    setSaving(true);
    setSaved(false);
    try {
      const res = await updateNotificationChannels({
        emailEnabled: ch.emailEnabled,
        smsEnabled: ch.smsEnabled,
        whatsappEnabled: false,
        smsEvents: ch.smsEvents,
        memberAttendanceEmailEnabled: enabled,
      });
      onSaved(res);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } finally {
      setSaving(false);
    }
  }

  async function sendNow() {
    setSending(true);
    setSentNote(null);
    try {
      const r = await sendMemberAttendanceEmailsNow();
      setSentNote(
        `Sent last week (${r.weekStart} – ${r.weekEnd}): ${r.emails} member emails across ${r.gyms} live gyms. Email only — no SMS.`,
      );
    } catch {
      setSentNote('Could not send the member attendance emails.');
    } finally {
      setSending(false);
    }
  }

  return (
    <Card className="mt-4 p-5">
      <h2 className="font-display text-lg font-semibold text-ink">Member check-in emails</h2>
      <p className="mt-0.5 text-sm text-muted">
        Every Monday at 09:00 we email members who still have gym access: praise if they showed up a
        lot last week, a nudge if they barely (or never) did. Live gyms only. Never SMS.
      </p>
      <div className="mt-4 flex items-center gap-3">
        <Toggle
          on={enabled}
          disabled={saving}
          onChange={() => setEnabled(!enabled)}
          label="Email members"
        />
        <span className="text-sm font-medium text-ink">Email members every Monday</span>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button size="sm" onClick={save} loading={saving}>
          Save member emails
        </Button>
        <Button size="sm" variant="outline" onClick={sendNow} loading={sending}>
          Send last week now
        </Button>
        {saved && <p className="text-sm font-medium text-ok-ink">Saved</p>}
      </div>
      {sentNote && <p className="mt-3 text-sm text-ink-soft">{sentNote}</p>}
    </Card>
  );
}

function Toggle({
  on,
  disabled,
  onChange,
  label,
}: {
  on: boolean;
  disabled?: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
        on ? 'bg-ink' : 'bg-line',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
      )}
    >
      <span
        className={cn(
          'inline-block size-5 transform rounded-full bg-white shadow transition-transform',
          on ? 'translate-x-[22px]' : 'translate-x-0.5',
        )}
      />
    </button>
  );
}
